// Hook: Multi-watchlist management with Firestore CRUD + live price merge
import { useState, useCallback, useEffect } from 'react';
import { useTrading } from '../context/TradingContext';
import { doc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { WS_MODES } from '../config/angelOne';

export function useTradingWatchlist() {
  const { subscribeTicks, unsubscribeTicks, getQuote, connectionStatus } = useTrading();
  const [watchlists, setWatchlists] = useState([
    { name: 'Watchlist 1', items: [] },
    { name: 'Watchlist 2', items: [] },
    { name: 'Watchlist 3', items: [] },
  ]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  // Load from Firestore
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) { setLoading(false); return; }

    const ref = doc(db, 'users', user.uid, 'tradingWatchlists', 'data');
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        if (data.watchlists) setWatchlists(data.watchlists);
        if (data.activeIndex !== undefined) setActiveIndex(data.activeIndex);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  // Subscribe to live prices for active watchlist
  useEffect(() => {
    if (connectionStatus !== 'connected') return;
    const items = watchlists[activeIndex]?.items || [];
    if (items.length === 0) return;

    const tokens = items.map(item => ({
      exchange: item.exchange,
      token: item.token,
    }));

    subscribeTicks(tokens, WS_MODES.QUOTE);
    return () => unsubscribeTicks(tokens);
  }, [connectionStatus, activeIndex, watchlists, subscribeTicks, unsubscribeTicks]);

  const save = useCallback(async (newWatchlists, newActiveIndex) => {
    const user = auth.currentUser;
    if (!user) return;
    const ref = doc(db, 'users', user.uid, 'tradingWatchlists', 'data');
    await setDoc(ref, {
      watchlists: newWatchlists,
      activeIndex: newActiveIndex ?? activeIndex,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  }, [activeIndex]);

  const addToWatchlist = useCallback(async (instrument, watchlistIndex = activeIndex) => {
    const newWatchlists = [...watchlists];
    const items = newWatchlists[watchlistIndex].items;
    const exists = items.some(i => i.token === instrument.token && i.exchange === instrument.exchange);
    if (exists) return;

    items.push({
      token: instrument.token,
      symbol: instrument.symbol || instrument.tradingsymbol,
      name: instrument.name || instrument.symbol,
      exchange: instrument.exch_seg || instrument.exchange,
    });
    setWatchlists(newWatchlists);
    await save(newWatchlists);
  }, [watchlists, activeIndex, save]);

  const removeFromWatchlist = useCallback(async (token, exchange, watchlistIndex = activeIndex) => {
    const newWatchlists = [...watchlists];
    newWatchlists[watchlistIndex].items = newWatchlists[watchlistIndex].items
      .filter(i => !(i.token === token && i.exchange === exchange));
    setWatchlists(newWatchlists);
    await save(newWatchlists);
  }, [watchlists, activeIndex, save]);

  const switchWatchlist = useCallback(async (index) => {
    setActiveIndex(index);
    await save(watchlists, index);
  }, [watchlists, save]);

  // Get live prices for current watchlist
  const itemsWithPrices = (watchlists[activeIndex]?.items || []).map(item => ({
    ...item,
    ...(getQuote(item.exchange, item.token) || {}),
  }));

  return {
    watchlists,
    activeIndex,
    activeWatchlist: watchlists[activeIndex],
    items: itemsWithPrices,
    loading,
    addToWatchlist,
    removeFromWatchlist,
    switchWatchlist,
  };
}
